import axios from "axios";
import { getMsalInstance } from "./msalConfig.js";

const MICROSOFT_SSO_REDIRECT_ERROR_KEY = "microsoftSsoRedirectError";

let microsoftBackendLoginStarted = false;

export function setMicrosoftSsoRedirectError(message) {
  sessionStorage.setItem(MICROSOFT_SSO_REDIRECT_ERROR_KEY, message ?? "");
}

export function consumeMicrosoftSsoRedirectError() {
  if (sessionStorage.getItem(MICROSOFT_SSO_REDIRECT_ERROR_KEY) === null) {
    return null;
  }

  const message = sessionStorage.getItem(MICROSOFT_SSO_REDIRECT_ERROR_KEY);
  sessionStorage.removeItem(MICROSOFT_SSO_REDIRECT_ERROR_KEY);
  return message;
}

export function resetMicrosoftSsoRedirectState() {
  microsoftBackendLoginStarted = false;
  sessionStorage.removeItem(MICROSOFT_SSO_REDIRECT_ERROR_KEY);
}

export function hasPendingMicrosoftSsoRedirect() {
  if (!process.env.REACT_APP_MICROSOFT_SSO_CLIENT_ID) {
    return false;
  }

  if (window.location.pathname !== "/login") {
    return false;
  }

  const hash = window.location.hash || "";
  const search = window.location.search || "";

  return (
    hash.includes("code=") ||
    hash.includes("error=") ||
    search.includes("code=") ||
    search.includes("error=")
  );
}

export function getSsoBackendErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof data === "string") {
    return data;
  }
  if (typeof data?.code === "string") {
    return data.code;
  }
  if (typeof data?.message === "string") {
    return data.message;
  }
  return null;
}

export function resolveAuthErrorMessage(error, t) {
  const data = error?.response?.data;
  const code =
    typeof data === "object" && data ? data.code || data.message : null;

  if (code === "invalid_password") {
    const count = Number(data?.remainingAttempts);
    return t("login.attemptsRemaining", {
      count: Number.isFinite(count) ? count : 0,
    });
  }
  if (code === "account_locked") {
    return t("login.accountLocked");
  }
  if (code === "invalid_credentials" || code === "missing_credentials") {
    return t("login.invalidCredentials");
  }
  if (typeof data === "string") {
    return data;
  }
  if (typeof data?.message === "string") {
    return data.message;
  }
  return null;
}

export async function handleMicrosoftSsoRedirect({ api, onSuccess, onError }) {
  if (!process.env.REACT_APP_MICROSOFT_SSO_CLIENT_ID) {
    return false;
  }

  const instance = await getMsalInstance();
  if (!instance) {
    return false;
  }

  let response;
  try {
    response = await instance.handleRedirectPromise();
  } catch (error) {
    if (error?.errorCode === "user_cancelled") {
      return false;
    }

    onError?.(error, getSsoBackendErrorMessage(error));
    return false;
  }

  if (!response) {
    return false;
  }

  if (microsoftBackendLoginStarted) {
    return false;
  }

  microsoftBackendLoginStarted = true;

  try {
    const responseBackend = await axios.post(
      `${api}auth/microsoft/callback`,
      { body: response.idToken },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    await onSuccess(responseBackend.data);
    return true;
  } catch (error) {
    microsoftBackendLoginStarted = false;

    if (error?.errorCode === "user_cancelled") {
      return false;
    }

    onError?.(error, getSsoBackendErrorMessage(error));
    return false;
  }
}
